import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import messages from "../../../assets/locale/messages";
import { UIActionModal, UICallout, UISelectCard } from "../../ui";
import {
  fillTemplate,
  formatAmount,
  getSubscriptionPlanName,
} from "../../Events/Form/Steps/ChooseCrewSize/crewSizeUtils";
import SummaryRow from "../../Events/Form/Steps/PublishAndPay/SummaryRow";
import { getPlanChangeDifference } from "./subscriptionPlanUtils";

import "../../Events/Form/Steps/PublishAndPay/PublishAndPay.scss";
import "../../Events/Event/Modals/_CrewSizeUpgradeModals.scss";
import "./Subscription.scss";

/**
 * "Upgrade Subscription Plan": the Super Admin moves a company to a plan above its current one.
 * Mounted on open, so no plan is selected each time it opens.
 */
const ChangeSubscriptionPlanModal = ({ subscription, plans, saving, onClose, onConfirm }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, companiesList } = messages[lang];
  const t = companiesList.subscription.modal;
  const [selectedId, setSelectedId] = useState(null);

  const currentPlan = subscription.subscription_plan;
  const selected = plans.find((plan) => plan.id === selectedId);
  const planName = (plan) => getSubscriptionPlanName(plan, lang);
  const difference = selected
    ? getPlanChangeDifference({ currentPlan, newPlan: selected })
    : null;

  return (
    <UIActionModal
      open
      variant="info"
      className="crew-size-upgrade-modal"
      title={t.title}
      onClose={() => !saving && onClose()}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={t.confirm}
          confirmDisabled={!selected}
          confirmLoading={saving}
          onCancel={() => !saving && onClose()}
          onConfirm={() => onConfirm(selected)}
        />
      }
    >
      <UIActionModal.Divider />
      <div className="crew-size-upgrade__body">
        <Typography component="p" className="text-body-sm crew-size-upgrade__message">
          {t.description}
        </Typography>
        <div className="crew-size-upgrade__section">
          <Typography component="p" className="text-label-md crew-size-upgrade__current">
            {t.current}{" "}
            <strong>
              {fillTemplate(t.currentValue, {
                plan: planName(currentPlan),
                price: formatAmount(currentPlan.price_per_year),
              })}
            </strong>
          </Typography>
          <div
            className="change-plan-modal__plans"
            role="radiogroup"
            aria-label={t.title}
          >
            {/* In the order the backend returns them: cheapest first (PROLINE-4150). */}
            {plans.map((plan) => (
              <UISelectCard
                key={plan.id}
                selectionRole="radio"
                size="md"
                title={planName(plan)}
                meta={[
                  fillTemplate(companiesList.subscription.yearlyPrice, {
                    price: formatAmount(plan.price_per_year),
                  }),
                ]}
                selected={plan.id === selectedId}
                onClick={() => setSelectedId(plan.id)}
              />
            ))}
          </div>
        </div>

        {selected && (
          <div className="crew-size-upgrade__summary">
            <SummaryRow
              label={t.newPlan}
              value={fillTemplate(t.newPlanValue, {
                plan: planName(selected),
                current: planName(currentPlan),
              })}
            />
            <SummaryRow
              label={t.price}
              value={fillTemplate(t.priceValue, {
                price: formatAmount(selected.price_per_year),
                plan: planName(selected),
              })}
            />
            <SummaryRow
              total
              label={t.difference}
              value={fillTemplate(t.differenceValue, { amount: formatAmount(difference) })}
            />
          </div>
        )}

        <UICallout
          variant="primary"
          hasBorder={false}
          className="change-plan-modal__notice"
          icon={<WarningAmberRoundedIcon fontSize="small" />}
          title={t.noticeTitle}
          description={t.noticeMessage}
        />
      </div>
    </UIActionModal>
  );
};

ChangeSubscriptionPlanModal.propTypes = {
  /** The company's subscription: its current plan. */
  subscription: PropTypes.shape({
    subscription_plan: PropTypes.object.isRequired,
  }).isRequired,
  /** The plans above the current one, as the backend returns them. */
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The change is being sent: the modal can't be confirmed or closed meanwhile. */
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called with the chosen plan. */
  onConfirm: PropTypes.func.isRequired,
};

ChangeSubscriptionPlanModal.defaultProps = {
  saving: false,
};

export default ChangeSubscriptionPlanModal;
